import { TIER_NAMES } from "MVVM/Utilities/SubscriptionTiers";
import {
  ICompetition,
  ICompetitionPrize,
  IClipRequirement,
  TSide,
} from "./@types";

const SIDE_LABELS: Record<TSide, string> = {
  T: "Terrorist",
  CT: "Counter-Terrorist",
};

const SLUG_LABEL_OVERRIDES: Record<string, string> = {
  ars: "ARs",
  smgs: "SMGs",
  lmgs: "LMGs",
  awp: "AWP",
  "ak-47": "AK-47",
  m4a4: "M4A4",
  "m4a1-s": "M4A1-S",
  "ssg-08": "SSG 08",
  "r8-revolver": "R8 Revolver",
};

const prettifySlug = (slug: string): string => {
  const override = SLUG_LABEL_OVERRIDES[slug.toLowerCase()];
  if (override) return override;
  return slug
    .split(/[-_\s]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const getClipRestrictionLabels = (
  clipReq: IClipRequirement | null | undefined,
): string[] => {
  if (!clipReq) return [];
  const labels: string[] = [];

  if (clipReq.map) labels.push(`${prettifySlug(clipReq.map)} only`);

  if (clipReq.weaponType) {
    labels.push(`${prettifySlug(clipReq.weaponType)} only`);
  } else if (clipReq.weapon) {
    labels.push(`${prettifySlug(clipReq.weapon)} only`);
  }

  if (clipReq.clutch) {
    labels.push("Clutch rounds only");
  } else if (clipReq.situation) {
    labels.push(`${prettifySlug(clipReq.situation)} only`);
  }

  if (clipReq.economy)
    labels.push(`${prettifySlug(clipReq.economy)} rounds only`);

  if (clipReq.side) labels.push(`${SIDE_LABELS[clipReq.side]} side only`);

  if (clipReq.command) labels.push(`Created with ${clipReq.command}`);

  return labels;
};

export const COMPETITION_PLACEHOLDER_BANNER =
  "https://placehold.co/1200x400/1a1a2e/ffffff?text=Competition";

export type TCompetitionPhase = "upcoming" | "active" | "ended";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const isEndingWithinDays = (
  competition: ICompetition,
  days: number,
): boolean => {
  const end = new Date(competition.endsAt).getTime();
  const diff = end - Date.now();
  return diff > 0 && diff <= days * DAY;
};

export const getCompetitionPhase = (
  competition: ICompetition,
): TCompetitionPhase => {
  const now = Date.now();
  const start = new Date(competition.startsAt).getTime();
  const end = new Date(competition.endsAt).getTime();
  if (end <= now) return "ended";
  if (start > now) return "upcoming";
  return "active";
};

const plural = (n: number, unit: string) => `${n} ${unit}${n !== 1 ? "s" : ""}`;

export const getCountdownLabel = (
  competition: ICompetition,
  phase: TCompetitionPhase = getCompetitionPhase(competition),
): string => {
  const now = Date.now();
  const start = new Date(competition.startsAt).getTime();
  const end = new Date(competition.endsAt).getTime();

  if (phase === "ended") {
    const date = new Date(end).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `Ended ${date}`;
  }

  if (phase === "upcoming") {
    const diff = start - now;
    const days = Math.ceil(diff / DAY);
    if (days > 1) return `Starts in ${plural(days, "day")}`;
    const hours = Math.ceil(diff / HOUR);
    return `Starts in ${plural(hours, "hour")}`;
  }

  const diff = end - now;
  const days = Math.floor(diff / DAY);
  const hours = Math.floor((diff % DAY) / HOUR);
  if (days > 0) return `${days}d ${hours}h left`;
  const minutes = Math.floor((diff % HOUR) / MINUTE);
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

export const getSubscriptionLabel = (prize: ICompetitionPrize): string => {
  if (!prize.tier || !prize.duration) return "Allstar Subscription";
  /* TIER_NAMES is the canonical user-facing tier map (ESSENTIALS → Basic,
   * PRO → Standard, CREATOR_PLAT → Unlimited). The previous local
   * TIER_DISPLAY here used the old internal codenames. */
  const tier = TIER_NAMES[prize.tier] ?? prize.tier;
  const duration = prize.duration === "year" ? "1 year" : "1 month";
  return `${duration} ${tier}`;
};

export type RankAccent = { color: string; bg: string; bgHover: string };

export const rankAccent = (
  rank: number | null,
  theme: { colors: Record<string, string> },
): RankAccent | null => {
  if (rank === 1)
    return {
      color: theme.colors.rare,
      bg: "rgba(208, 106, 174, 0.14)",
      bgHover: "rgba(208, 106, 174, 0.22)",
    };
  if (rank === 2)
    return {
      color: theme.colors.turbo,
      bg: "rgba(113, 170, 255, 0.12)",
      bgHover: "rgba(113, 170, 255, 0.2)",
    };
  if (rank === 3)
    return {
      color: theme.colors.envy,
      bg: "rgba(77, 189, 148, 0.12)",
      bgHover: "rgba(77, 189, 148, 0.2)",
    };
  return null;
};

export const getPrizeLabel = (
  prizes: ICompetitionPrize[] | null | undefined,
): string => {
  if (!prizes || prizes.length === 0) return "Just for fun";
  const first = prizes[0];
  if (first.description) return first.description;
  if (first.prizeType === "subscription") return getSubscriptionLabel(first);
  if (first.prizeType === "custom") return "Custom Prize";
  return "Just for fun";
};
